import { Box, BoxProps, Card, CardBody, CardFooter, CardHeader, Spinner, Text, TextProps } from 'grommet';
import { ColorType } from 'grommet/utils';
import { ReactNode } from 'react';
import { AddNewButton, Delete, SaveStatus, SaveStatusIndicator } from '..';

export interface ITalonCardProps extends React.PropsWithChildren {
  newItem?: () => void;
  disableAdd?: boolean;
  deleteItems?: () => Promise<void>;
  label: ReactNode | string;
  labelPlural?: string;
  addIcon?: JSX.Element;
  buttons?: React.ReactNode[];
  selectedNames?: string;
  headerChildren?: React.ReactNode;
  saveStatus?: SaveStatus;
  cardProps?: BoxProps;
  titleTextProps?: TextProps;
  margin?: BoxProps['margin'];
  headerHeight?: BoxProps['height'];
  headerBackground?: ColorType;
  bodyBackground?: BoxProps['background'];
  footerBackground?: BoxProps['background'];
  color?: TextProps['color'];
  hideFooter?: boolean;
  animate?: boolean;
  isLoading?: boolean;
  onHeaderClick?: () => void;
  headerCardProps?: BoxProps;
  addButtonLabel?: string;
}

export const TalonCard = ({
  newItem,
  deleteItems,
  disableAdd,
  label,
  labelPlural,
  addIcon,
  buttons,
  children,
  headerChildren,
  selectedNames,
  saveStatus,
  cardProps,
  titleTextProps,
  margin = 'small',
  headerHeight,
  headerBackground,
  bodyBackground,
  footerBackground,
  color = 'accent-1',
  hideFooter = false,
  animate = false,
  isLoading = false,
  onHeaderClick,
  headerCardProps,
  addButtonLabel = 'Add New',
}: ITalonCardProps) => {
  /** Context **/

  /** State **/

  /** Render **/
  return (
    <Card
      margin={margin}
      //  background="light-1"
      {...cardProps}
    >
      <CardHeader
        gap="small"
        background={headerBackground || { color: 'brand-gradient', dark: true }}
        style={{ background: 'var(--brand-gradient)', zIndex: 2 }}
        height={headerHeight || '60px'}
        pad={{ horizontal: 'small' }}
        // onClick={onHeaderClick}
        elevation="xsmall"
        {...headerCardProps}
      >
        <Box
          direction="row"
          gap="small"
          flex="grow"
          align="center"
          style={{ zIndex: 2, cursor: onHeaderClick ? 'pointer' : undefined }}
          onClick={onHeaderClick}
        >
          <Text weight={600} {...titleTextProps} color={color}>
            {labelPlural || label}
          </Text>
          <SaveStatusIndicator saveStatus={saveStatus} />
        </Box>
        {headerChildren}
      </CardHeader>
      <CardBody
        flex="grow"
        align={isLoading ? 'center' : undefined}
        pad={isLoading ? 'medium' : undefined}
        background={bodyBackground || 'accent-1'}
        elevation="xsmall"
        style={{ zIndex: 1 }}
      >
        {isLoading && <Spinner />}
        <Box className={animate ? `animate-height ${isLoading ? 'collapsed' : 'expanded'}` : ''}>
          {!isLoading && children}
        </Box>
      </CardBody>
      {!hideFooter && (
        <CardFooter gap="xxsmall" background={footerBackground || 'light-1'} justify="end" pad="xsmall">
          {buttons}
          {newItem && (
            <AddNewButton
              onClick={newItem}
              tipText={typeof label === 'string' ? `New ${label}` : addButtonLabel}
              icon={addIcon}
              disabled={disableAdd}
            />
          )}
          {deleteItems && (
            <Delete
              onClick={() => deleteItems()}
              name={selectedNames || 'these items'}
              disabled={!selectedNames}
              hoverIndicator
            />
          )}
        </CardFooter>
      )}
    </Card>
  );
};
